import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
// Same icon components as the sidebar nav items, so the two stay aligned:
// scaffolder uses AddCircleOutline, catalog uses Category, techdocs uses
// the core DocsIcon.
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CategoryIcon from '@material-ui/icons/Category';
import { DocsIcon } from '@backstage/core-components';
import { FONT_DISPLAY, brandGradient } from '../../theme/themes';
import { HomeCounts, useKindCounts } from './data';
import { eyebrowSx } from './HomeCard';
import { pluralize } from './viewModel';

// The three main journeys, surfaced as cards with live catalog counts. The
// brand gradient is reserved for the creation card (echoes scaffolder tiles).

type ActionCard = {
  title: string;
  to: string;
  body: string;
  icon: ReactNode;
  create?: boolean;
  count: (counts: HomeCounts) => string;
};

const CARDS: ActionCard[] = [
  {
    title: 'Self Services',
    to: '/create',
    body: 'Scaffold a new PANDA squad or component from a reviewed template.',
    icon: <AddCircleOutlineIcon style={{ fontSize: 20 }} />,
    create: true,
    count: c => pluralize(c.templates, 'template'),
  },
  {
    title: 'Catalog',
    to: '/catalog',
    body: 'Every squad, component and API, with ownership and docs attached.',
    icon: <CategoryIcon style={{ fontSize: 20 }} />,
    count: c =>
      `${pluralize(c.components, 'component')} · ${pluralize(c.apis, 'API')}`,
  },
  {
    title: 'Documentation',
    to: '/docs',
    body: 'TechDocs for the platform and every squad, deployment guide included.',
    icon: <DocsIcon fontSize="small" />,
    count: c => pluralize(c.docs, 'doc site'),
  },
];

const cardTitleSx = {
  fontFamily: FONT_DISPLAY,
  fontWeight: 750,
  fontSize: 16.5,
  letterSpacing: '-0.01em',
  m: 0,
} as const;

export const ActionCards = () => {
  const { counts, loading } = useKindCounts();
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2.25,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
      }}
    >
      {CARDS.map(card => (
        <Paper
          key={card.title}
          component={RouterLink}
          to={card.to}
          elevation={1}
          sx={{
            borderRadius: '10px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            textDecoration: 'none',
            color: 'inherit',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 },
            '&:hover .actionArrow': { transform: 'translateX(3px)' },
          }}
        >
          {card.create ? (
            <Box
              sx={{
                background: brandGradient(theme.palette.mode),
                borderRadius: '10px 10px 0 0',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2.5,
                py: 1.75,
              }}
            >
              {card.icon}
              <Typography component="h2" sx={cardTitleSx}>
                {card.title}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2.5,
                pt: 2.25,
              }}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '9px',
                  bgcolor: alpha(theme.palette.primary.main, 0.09),
                  color: 'primary.main',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                }}
              >
                {card.icon}
              </Box>
              <Typography component="h2" sx={cardTitleSx}>
                {card.title}
              </Typography>
            </Box>
          )}
          <Typography
            sx={{
              px: 2.5,
              pt: card.create ? 1.75 : 0.75,
              color: 'text.secondary',
              fontSize: 13.5,
              flex: 1,
            }}
          >
            {card.body}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 1.75,
            }}
          >
            {loading && <Skeleton width={110} sx={{ fontSize: 11 }} />}
            {!loading && counts && (
              <Typography
                component="span"
                sx={{ ...eyebrowSx, fontSize: 10.5 }}
              >
                {card.count(counts)}
              </Typography>
            )}
            {!loading && !counts && <span />}
            <ArrowForwardRoundedIcon
              className="actionArrow"
              sx={{
                fontSize: 17,
                color: 'primary.main',
                transition: 'transform 0.15s ease',
              }}
            />
          </Box>
        </Paper>
      ))}
    </Box>
  );
};
